import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RotateCcw, Timer, Upload } from "lucide-react";

export const RESUME_LABEL = {
    datalake_text: "saved text",
    uploaded_file: "stored upload",
    source_url: "source URL",
};

// Actions cell of the Ingest Job Manager: auto-retry countdown, Retry button, or re-upload hint.
export const JobRetryCell = ({ job, onRetried }) => {
    const [busy, setBusy] = useState(false);

    if (job.status === "queued" && job.next_retry_at) {
        return (
            <span data-testid={`ij-auto-retry-${job.job_id}`} title={job.last_error || ""}
                  className="inline-flex items-center gap-1 text-[10px] font-mono text-sky-200/85 whitespace-nowrap">
                <Timer className="w-3 h-3" strokeWidth={2} />
                auto-retry {job.auto_retries}/{job.auto_retry_max} · {new Date(job.next_retry_at).toLocaleTimeString()}
            </span>
        );
    }
    if (job.status !== "failed") return <span className="text-white/25 text-[11px]">—</span>;
    if (!job.retryable) {
        return (
            <span data-testid={`ij-reupload-${job.job_id}`} title="The uploaded file is no longer on the server"
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-200/80 whitespace-nowrap">
                <Upload className="w-3 h-3" strokeWidth={2} /> Re-upload required
            </span>
        );
    }

    const retry = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/ingest-jobs/${job.job_id}/retry`);
            toast.success(`Retry queued — resuming from ${RESUME_LABEL[data.resume_from] || "the saved input"}.`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Retry failed");
        } finally {
            setBusy(false);
            onRetried?.();
        }
    };

    return (
        <div className="flex flex-col items-start gap-0.5">
            <button onClick={retry} disabled={busy} data-testid={`ij-retry-${job.job_id}`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#7C3AED]/50 text-[#d8c4ff] text-[11px] hover:bg-[#7C3AED]/15 transition-colors disabled:opacity-50">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" strokeWidth={2} />}
                Retry
            </button>
            <span className="text-[10px] text-white/35 font-mono" data-testid={`ij-resume-from-${job.job_id}`}>
                from {RESUME_LABEL[job.resume_from] || job.resume_from}
            </span>
        </div>
    );
};
